import { useCallback, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { useBudget } from "../../../../contexts/BudgetContext/projectbudgets.context";
import { useServices } from "../../../../contexts/ServicesContext/projectservices.context";
import {
  getCategoriesWithServices,
  mergeEnabledCategoryKeysFromServiceItemTypeList,
  withDraftSortOrder,
} from "../../../../utils/budget-import-helper";
import { BudgetType, CalcOption } from "../../../../types/budget/budget";

/**
 * "Novo Orçamento": abre o modal de importação e cria o orçamento a partir do
 * serviço do projeto (ou vazio).
 *
 * Estava copiado no RightSideBar e no estado vazio do ActiveBudget; com o botão
 * também na barra de ações seriam três cópias da mesma regra de montagem do
 * documento. Quem consome só precisa do `open` e de espalhar `modalProps` no
 * `ImportServicesModal`.
 */
export function useCreateBudgetFlow() {
  const { createBudget, createEmptyBudget } = useBudget();
  const { activeService } = useServices();
  const [isOpen, setIsOpen] = useState(false);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);

  const hasEnabledCategories = useMemo(
    () =>
      Object.keys(
        mergeEnabledCategoryKeysFromServiceItemTypeList(
          activeService?.itemTypeList
        )
      ).length > 0,
    [activeService?.itemTypeList]
  );

  const categoriesWithServices = useMemo(
    () => getCategoriesWithServices(activeService),
    [activeService]
  );

  const handleImportWithMapping = useCallback(
    (
      _mapping: Map<string, CalcOption>,
      mode: "services" | "categories_only",
      type: BudgetType
    ) => {
      if (type === BudgetType.LOGISTICA) {
        createEmptyBudget(type);
        close();
        return;
      }

      if (!activeService || !activeService._id) {
        toast.warn("Nenhum serviço encontrado para importar");
        return;
      }

      const { _id, name, budgetServices, ...serviceDataWithoutId } =
        activeService;
      const categoriesOnly = mode === "categories_only";

      createBudget({
        ...serviceDataWithoutId,
        budgetServices: categoriesOnly
          ? []
          : withDraftSortOrder(budgetServices),
        ...(categoriesOnly
          ? {
              addedItemTypeList: [],
              supplierList: [],
              baseValues: [],
              mainAppliedTaxes: [],
              files: [],
              paymentSolicitation: [],
            }
          : {}),
        type,
        status: "OPEN" as const,
        versionHistory: [{ status: "OPEN", version: 0, comparisonVersion: 0 }],
        budgetIndex: 0,
      });
      close();
    },
    [activeService, createBudget, createEmptyBudget, close]
  );

  const handleCreateEmpty = useCallback(
    (type: BudgetType) => {
      createEmptyBudget(type);
      close();
    },
    [createEmptyBudget, close]
  );

  return {
    open,
    modalProps: {
      isOpen,
      onClose: close,
      onImport: handleImportWithMapping,
      onCreateEmpty: handleCreateEmpty,
      categoriesWithServices,
      hasEnabledCategories,
      hasDraftItems: (activeService?.budgetServices?.length || 0) > 0,
    },
  };
}
